import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button, message, Popconfirm, Tooltip } from "src/assets/imports/antdComponents";
import { IResponse } from "src/pages/Studies/models/interfaces";
import { RootState } from "src/store";
import { formatAutoSaveTime } from "../../transformer";
import { deleteReport } from "../../serviceHelper";
import { deleteSelectedReport } from "../../states/reportsSlice";
import { AutosaveStatusType } from "../../models/interface";

const ReportsTopBarActions = ({ collabContent, handleManualSave = () => {} }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();
	const { metaDetails } = useSelector((state: RootState) => state.studiesSlice);
	const { selectedReport, autosaveStatus, createReportForm, isLockedByAnotherUser } = useSelector((state: RootState) => state.reportsSlice);
	const [isDeleting, setIsDeleting] = React.useState(false);

	const hasDeleteAccess =
		selectedReport?.permissions?.collaborators?.find((collaborator) => collaborator?.collaborator_id?.toLowerCase() === metaDetails?.id?.toLowerCase())?.is_owner ?? false;

	const handleDelete = async () => {
		if (!hasDeleteAccess) return;
		setIsDeleting(true);
		try {
			const res: IResponse = await deleteReport({ report_gen_id: selectedReport?.report_gen_id, report_iteration_id: selectedReport?.report_iteration_id });
			if (res?.message) {
				dispatch(deleteSelectedReport({ report_gen_id: selectedReport?.report_gen_id, report_iteration_id: selectedReport?.report_iteration_id }));
				message.success(t("Report deleted successfully"));
				navigate("/reports");
			}
		} catch (error) {
			console.error("Error deleting report:", error);
		} finally {
			setIsDeleting(false);
		}
	};

	const renderAutosaveStatus = React.useCallback(() => {
		let icon, statusClass, statusText, clickAction, statusTooltip;

		switch (autosaveStatus?.status) {
			case AutosaveStatusType.Saving:
				icon = "save_clock";
				statusClass = "secondary-color opacity-70";
				statusText = t("Autosaving...");
				clickAction = handleManualSave;
				statusTooltip = t("Click to save now");
				break;
			case AutosaveStatusType.UnsavedChanges:
				icon = "save_clock";
				statusClass = "secondary-color opacity-70";
				statusText = t("Your changes will be autosaved");
				clickAction = handleManualSave;
				statusTooltip = t("Click to save now");
				break;
			case AutosaveStatusType.ValidationError:
				icon = "cloud_off";
				statusClass = "text-[--warning-red]";
				statusText = t("Autosave paused - validation error");
				statusTooltip = t("Fix validation errors to resume autosave");
				clickAction = undefined;
				break;
			case AutosaveStatusType.Saved:
				icon = "save_clock";
				statusClass = "primary-color";
				statusText = `${t("Autosaved")} ${formatAutoSaveTime(autosaveStatus?.lastSaved)}`;
				statusTooltip = t("All changes saved");
				clickAction = undefined;
				break;
			case AutosaveStatusType.ToBeSaved:
				icon = "save_clock";
				statusClass = "secondary-color opacity-70";
				statusText = t("Your changes will be autosaved");
				clickAction = undefined;
				statusTooltip = undefined;
				break;
			case AutosaveStatusType.Error:
				icon = "cloud_off";
				statusClass = "text-[--warning-red]";
				statusText = t("Autosave failed");
				statusTooltip = t("Click to retry");
				clickAction = handleManualSave;
				break;
			default:
				return null;
		}

		if (!icon || !statusText) return null;

		return (
			<div className={`flex items-center cursor-default`} onClick={() => clickAction && clickAction()} title={statusTooltip}>
				<i className={`material-symbols-outlined text-2xl mr-2 ${statusClass}`}>{icon}</i>
				<span className={`text-base-1 font-normal word-spacing-2 underline ${statusClass}`}>{statusText}</span>
			</div>
		);
	}, [autosaveStatus?.status, autosaveStatus?.lastSaved, handleManualSave]);

	const shouldShowAutosaveStatus = useMemo(() => {
		return location.pathname === "/add-report" && selectedReport?.report_iteration_id && selectedReport?.report_gen_id && createReportForm?.file_type;
	}, [selectedReport?.report_iteration_id, selectedReport?.report_gen_id, createReportForm?.file_type]);

	return (
		<div className="flex items-center gap-4">
			{isLockedByAnotherUser?.status ? (
				<Tooltip title={`Locked by ${isLockedByAnotherUser.user?.user_name}`} placement="left">
					<i className="material-symbols-outlined text-[1.8rem] secondary-color">lock</i>
				</Tooltip>
			) : (
				<div className="text-[x-small] text-[--neutral-grey]">{shouldShowAutosaveStatus && renderAutosaveStatus()}</div>
			)}
			{selectedReport?.report_gen_id ? (
				<div className="flex items-center gap-2">
					{collabContent}
					<Popconfirm title={t("Confirm Deletion?")} placement="leftBottom" onConfirm={handleDelete} okText={t("Yes")} cancelText={t("No")} disabled={isDeleting}>
						<Tooltip title={!hasDeleteAccess && t("No access")}>
							<Button type="text" className="p-0 w-[2.5rem] h-[2.5rem]" disabled={isDeleting || !hasDeleteAccess}>
								<i className={`material-symbols-outlined text-2xl text-[--warning-red] ${!hasDeleteAccess ? "cursor-not-allowed opacity-50" : ""}`}>delete</i>
							</Button>
						</Tooltip>
					</Popconfirm>
				</div>
			) : null}
		</div>
	);
};

export default ReportsTopBarActions;
